/**
 * "Type it. Wear it. Love it" - spells a word out of Jibbitz letter charms and
 * adds one line item per character.
 *
 * The letter catalogue arrives one of two ways:
 *  - a collection is configured -> rendered inline as JSON by
 *    snippets/jibbitz-letters.liquid, so typing costs no requests;
 *  - no collection -> fetched once, on first focus, from the search JSON view
 *    (templates/search.letters.liquid).
 * Both deliver the same shape, so everything below this is path-agnostic.
 *
 * Adds go to /cart/add.js, which means the "Added to cart" notification
 * (atc-toast.js) picks them up for free.
 */
if (!customElements.get('jibbitz-letters')) {
  /**
   * Variant titles are merchant-entered, so they can carry quotes ('6" Wide').
   * @param {string} value - Raw text.
   * @returns {string} Safe to drop inside a double-quoted attribute.
   */
  const escapeAttr = (value) => String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

  /**
   * Brings the header cart count and cart drawer in sync after an AJAX add.
   * Prefers the drawer's own refresh (it re-renders both the drawer body and
   * the cart-icon-bubble); falls back to updating just the count when the store
   * uses a cart page instead of a drawer.
   */
  async function refreshCart() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.refresh === 'function') {
      await drawer.refresh(true);
      return;
    }
    try {
      const res = await fetch('?sections=cart-icon-bubble');
      const sections = await res.json();
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        bubble.innerHTML = sections['cart-icon-bubble'];
      }
    } catch (e) {
      // Non-fatal: the item is in the cart; only the badge is stale until the
      // next navigation.
    }
  }

  class JibbitzLetters extends HTMLElement {
    connectedCallback() {
      this.maxChars = parseInt(this.dataset.max, 10) || 12;
      this.input = this.querySelector('.jibbitz-letters__input');
      this.errorEl = this.querySelector('.jibbitz-letters__error');
      this.errorText = this.querySelector('[data-error-text]');
      this.previewWrap = this.querySelector('[data-preview-wrap]');
      this.preview = this.querySelector('[data-preview]');
      this.addBtn = this.querySelector('[data-add]');
      this.styles = this.querySelector('[data-styles]');
      this.styleRow = this.querySelector('[data-style-row]');

      this.chars = [];
      this.style = null;
      this.letters = null;
      this.loading = null;

      const inline = this.querySelector('[data-letters]');
      if (inline) {
        // A collection is configured, so the catalogue is already in the page.
        this.setLetters(inline.textContent);
      } else if (this.dataset.search) {
        // No collection: fetch on first focus rather than on every product page
        // view, so browsers that never touch the block pay nothing.
        this.input.addEventListener('focus', () => this.load(), { once: true });
      }

      this.input.addEventListener('input', this.onInput.bind(this));
      this.addBtn.addEventListener('click', this.onAdd.bind(this));
    }

    /**
     * @param {string} json - Letter catalogue, as rendered by Liquid.
     * @returns {boolean} Whether a usable catalogue was parsed.
     */
    setLetters(json) {
      try {
        return this.setLettersData(JSON.parse(json));
      } catch (e) {
        return false;
      }
    }

    /**
     * @param {object[]} parsed - One or more catalogues already parsed. When a
     *   character appears in more than one (a number can surface under both the
     *   "LETTER" and "NUMBER" searches), the first occurrence wins - they're the
     *   same product, so it doesn't matter which.
     * @returns {boolean} Whether a usable catalogue resulted.
     */
    setLettersData(parsed) {
      if (!Array.isArray(parsed) || !parsed.length) return false;
      this.letters = new Map();
      parsed.forEach((l) => {
        if (l && l.key && !this.letters.has(l.key)) this.letters.set(l.key, l);
      });
      if (!this.letters.size) return false;
      this.buildStyles();
      return true;
    }

    /**
     * Fetches the catalogue via the search JSON view. data-search is a JSON
     * array of URLs (one search per category - letters, numbers, symbols) whose
     * results are merged, because a single search caps at 50 relevance-ranked
     * results and would drop whole categories. Cached after the first call, and
     * safe to call concurrently -- everyone awaits one request.
     * @returns {Promise<boolean>} Whether letters are available.
     */
    load() {
      if (this.letters) return Promise.resolve(true);
      if (this.loading) return this.loading;

      let urls;
      try {
        urls = JSON.parse(this.dataset.search);
        if (!Array.isArray(urls)) urls = [this.dataset.search];
      } catch (e) {
        urls = [this.dataset.search];
      }

      this.setBusy(true);
      this.loading = Promise.all(
        urls.map((url) =>
          fetch(url)
            // The view renders through the HTML layout, so it arrives as
            // text/html and .json() would reject on the content type.
            .then((r) => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
            .then((text) => JSON.parse(text))
            // One failed category shouldn't sink the rest.
            .catch(() => [])
        )
      )
        .then((lists) => this.setLettersData([].concat(...lists)))
        .catch(() => false)
        .finally(() => this.setBusy(false));

      return this.loading;
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      this.input.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    /**
     * Renders the style picker from the styles the letter products actually
     * offer. Only styles available on *every* letter are shown - a style that
     * exists for "A" but not "B" can't spell most words, and silently
     * substituting a different style for one letter would be worse.
     */
    buildStyles() {
      if (!this.styleRow) return;

      const all = [...this.letters.values()];
      const names = all[0].variants.map((v) => v.style);
      const shared = names.filter((name) =>
        all.every((letter) => letter.variants.some((v) => v.style === name))
      );

      // "Default Title" is Shopify's placeholder for a product with no options.
      if (shared.length < 2 || shared[0] === 'Default Title') return;

      this.style = shared[0];

      // The legend is only correct once we know the products have options; the
      // option name comes from the data, not from a hardcoded guess.
      const legend = this.querySelector('[data-style-legend]');
      if (legend && all[0].option) legend.textContent = `Choose ${all[0].option}:`;

      // One name for the whole row, unique per block so two of these on a page
      // don't share a radio group.
      const group = `jibbitz-style-${this.dataset.blockId || 'a'}`;
      this.styleRow.innerHTML = shared
        .map((name, i) => {
          const swatch = all[0].variants.find((v) => v.style === name);
          const media = swatch && swatch.image
            ? `<img src="${swatch.image}" alt="" width="44" height="44">`
            : `<span class="jibbitz-letters__style-text">${name}</span>`;
          return [
            '<label class="jibbitz-letters__style">',
            `<input type="radio" name="${group}" value="${escapeAttr(name)}"${i === 0 ? ' checked' : ''}>`,
            `<span class="jibbitz-letters__style-media" title="${escapeAttr(name)}">${media}</span>`,
            '</label>'
          ].join('');
        })
        .join('');

      this.styleRow.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', () => {
          this.style = radio.value;
          this.render();
        });
      });

      this.styles.hidden = false;
    }

    /**
     * @param {string} char - A single uppercase character.
     * @returns {object|null} The variant to add for the current style.
     */
    variantFor(char) {
      const letter = this.letters.get(char);
      if (!letter) return null;
      if (!this.style) return letter.variants[0];
      return letter.variants.find((v) => v.style === this.style) || letter.variants[0];
    }

    async onInput() {
      const raw = this.input.value.toUpperCase().slice(0, this.maxChars);
      if (this.input.value !== raw) this.input.value = raw;

      // Typing can beat the fetch (or start it, if focus never fired).
      if (!this.letters) {
        const ok = await this.load();
        if (!ok) {
          this.setError('Sorry, the letter charms could not be loaded. Please refresh and try again.');
          return;
        }
        // The value may have moved on while the request was in flight.
        if (this.input.value !== raw) return;
      }

      // Spaces are a legitimate word break, not an error - they just don't
      // produce a charm.
      const typed = [...raw].filter((c) => c !== ' ');
      const unsupported = [...new Set(typed.filter((c) => !this.letters.has(c)))];
      const soldOut = [...new Set(typed.filter((c) => {
        const v = this.variantFor(c);
        return v && !v.available;
      }))];

      this.chars = typed.filter((c) => this.letters.has(c) && this.variantFor(c).available);

      let error = '';
      if (unsupported.length) {
        error = `Sorry, we don't have a charm for ${unsupported.join(', ')}. Please edit it and try again.`;
      } else if (soldOut.length) {
        error = `${soldOut.join(', ')} is out of stock right now.`;
      }
      this.setError(error);
      this.render();
    }

    setError(message) {
      this.errorText.textContent = message;
      this.errorEl.hidden = !message;
      this.input.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    render() {
      this.preview.innerHTML = this.chars
        .map((c) => {
          const v = this.variantFor(c);
          const letter = this.letters.get(c);
          return [
            '<li class="jibbitz-letters__charm">',
            `<img src="${v.image || letter.image}" alt="${c}" width="48" height="48" loading="lazy">`,
            '</li>'
          ].join('');
        })
        .join('');

      this.previewWrap.hidden = !this.chars.length;
      this.addBtn.disabled = !this.chars.length || !this.errorEl.hidden;
    }

    async onAdd() {
      if (!this.chars.length) return;

      // Repeated letters ("HELLO") are one line item with quantity 2, not two
      // line items Shopify would merge anyway.
      const quantities = new Map();
      this.chars.forEach((c) => {
        const { id } = this.variantFor(c);
        quantities.set(id, (quantities.get(id) || 0) + 1);
      });

      this.addBtn.classList.add('is-loading');
      this.addBtn.disabled = true;

      try {
        const response = await fetch(`${(window.theme && window.theme.routes.cartAdd) || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [...quantities].map(([id, quantity]) => ({ id, quantity }))
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message);

        // The AJAX add doesn't touch the header count or cart drawer on its own,
        // so mirror what product-form.js does: let the drawer re-render itself.
        await refreshCart();

        this.input.value = '';
        this.chars = [];
        this.render();
      } catch (e) {
        this.setError(e.message || 'Sorry, these charms could not be added.');
      } finally {
        this.addBtn.classList.remove('is-loading');
        this.addBtn.disabled = !this.chars.length;
      }
    }
  }

  customElements.define('jibbitz-letters', JibbitzLetters);
}
